<script>
    import { onMount } from "svelte";

    import { v4 } from "uuid";

    import {
        DateInput,
        NotificationModal,
        SvelteTable,
    } from "../../../../Components/Common/index.svelte";

    import {
        getAmountInCurrency,
        getHHMM,
        getText,
        getYYYYMMDD,
        isObjectEmpty,
        showInfo,
    } from "../../../../Utils/utilFunctions.svelte";

    import ModifyFreeTextItemModal from "./freeTextComponents/ModifyFreeTextItemModal.svelte";

    import ChangeItemTypeModal from "./common/ChangeItemTypeModal.svelte";
    import {
        addIcon,
        deleteIcon,
        editIcon,
        FallBackEncounterDurationMins,
    } from "../../../../Utils/utilConstants.svelte";

    import { getSumOfColumnValues } from "../../helpers/encounterHelper.svelte";

    import { DirectInsuranceType } from "../../../../Utils/utilEnums.svelte";

    export let itemCodeObj = {},
        patientEncounterDetails = {},
        itemsData = [],
        procedureName = "",
        hasValidBillingData = false,
        isSelectorOpen = false,
        isTypeA = false,
        allowAsaCodes = false,
        isSavingItems = false,
        itemType = '',
        saveBillingItem = () => {},
        changeItemType = () => {},
        closeModal = () => {};

    $: btnOptions = [
        {
            class: "btn btn-error min-w-[100px]",
            loading: isSavingItems,
            text: "Cancel",
            function: () => closeModal(),
        },
        {
            class: "btn btn-secondary min-w-[100px]",
            loading: isSavingItems,
            text: isSavingItems ? "Saving" : "Save & Refresh",
            function: () =>
                saveBillingItem(selectedItems, "add", itemCodeObj, timeObj),
        },
    ];

    $: headerOptions = [
        {
            optionText: "Change to MBS Codes",
            iFont: "fa-solid fa-right-left",
            function: () => _openSwitchItemTypeModal(0),
            isDisabled: hasValidBillingData || isTypeA,
            disabledTooltip: `You cannot change codes when ${itemType} is already created`
        },
        {
            optionText: "Change to ASA Codes",
            iFont: "fa-solid fa-right-left",
            function: () => _openSwitchItemTypeModal(1),
            isDisabled: hasValidBillingData || isTypeA || !allowAsaCodes,
            disabledTooltip: `You cannot change codes when ${itemType} is already created`
        },
    ];

    $: columns = [
        {
            key: "freeTextDescription",
            title: "Item Description",
            headerClass: "text-left pl-3 cursor-default w-[70%] rounded-tl-xl",
            class: "text-left px-3 cursor-default w-[70%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? "Total" : getText(v.encounterItemDescription)}</p>`,
        },
        {
            key: "freeTextFeeCharged",
            title: "Fee Charged",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl w-[20%]",
            class: "text-left px-3 cursor-default w-[20%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.feeCharged) : getAmountInCurrency(v.feeCharged)}</p>`,
        },
        {
            key: "actions",
            title: "",
            headerClass: "text-center px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) => (v.isLast ? "" : _renderActionIcons()),
        },
    ];

    $: showChargePatientSelector = itemCodeObj.itemType == "quote";

    const __DEFAULT_ITEM_TYPE__ = Number(2);

    let selectedItems = [],
        selectedItem = {};

    let isSwitchItemTypeModalOpen = false,
        selectedItemType = __DEFAULT_ITEM_TYPE__;

    let showModifyItemForm = false;

    let timeObj = {},
        dischargeDateSelectorRefresh;

    let freeTextWithGstCharge = true;


    onMount(() => {
        if (isSelectorOpen) {
            selectedItems = [
                ...itemsData.map((item) => {
                    // in case copy items from quote to invoice has neither id nor tempId
                    if (!item.id && !item.tempId) return { ...item, tempId: v4() };
                    return item;
                }),
            ];
            const { insuranceType } = itemCodeObj;

            if (
                insuranceType &&
                insuranceType == DirectInsuranceType.ThirdPartyInsurer
            ) {
                const { patientEncounterThirdPartyInsurerData } =
                    patientEncounterDetails;

                freeTextWithGstCharge =
                    patientEncounterThirdPartyInsurerData?.addGST ?? false;
            }
        } else {
            selectedItems = [];
            freeTextWithGstCharge = true;
        }
        
        if(itemCodeObj.itemType == "invoice") {
            //loa start and end times
            const { startDate, endDate } = itemCodeObj;

            if (startDate) {
                timeObj.startTime = startDate.split("T")[1];
            }
            if (endDate) {
                timeObj.endTime = endDate.split("T")[1];
            }
        }
    });

    const modifyItem = (itemObj = {}) => {
        if (itemObj.hasOwnProperty("id"))
            selectedItems = selectedItems.map((item) =>
                item.id === itemObj.id ? itemObj : item,
            );
        else if (itemObj.hasOwnProperty("tempId"))
            selectedItems = selectedItems.map((item) =>
                item.tempId === itemObj.tempId ? itemObj : item,
            );
        else selectedItems = [...selectedItems, { ...itemObj, tempId: v4() }];

        _closeModifyItemForm();
    };

    const confirmSwitchItemType = (newItemType = Number(0)) => {
        changeItemType(newItemType);
        _closeSwitchItemTypeModal();
    };

    const handleEditItem = (item = {}) => {
        if (!item || isObjectEmpty(item)) return;

        _openModifyItemForm(item);
    };

    const handleRemoveItem = (item = {}) => {
        if (!item || isObjectEmpty(item)) return;

        if (item.hasOwnProperty("id"))
            selectedItems = selectedItems.filter((i) => i.id !== item.id);
        else if (item.hasOwnProperty("tempId"))
            selectedItems = selectedItems.filter(
                (i) => i.tempId !== item.tempId,
            );
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "actions") {
            selectedItem = { ...rowData };

            if (className.includes("addIcon")) handleEditItem(rowData);
            else if (className.includes("deleteIcon"))
                handleRemoveItem(rowData);
        }
    };

    const compareAndFixDischargeDate = () => {
        if (!timeObj.startDate || !timeObj.startTime) return;

        if (!timeObj.endDate || !timeObj.endTime) return;

        const startDateTime = new Date(
            `${timeObj.startDate}T${timeObj.startTime}`,
        );
        const endDateTime = new Date(`${timeObj.endDate}T${timeObj.endTime}`);

        if (startDateTime > endDateTime) {
            const newEndDateDT = new Date(
                startDateTime.setTime(
                    startDateTime.getTime() +
                        FallBackEncounterDurationMins * 60 * 1000,
                ),
            );
            timeObj.endDate = getYYYYMMDD(newEndDateDT);
            timeObj.endTime = getHHMM(newEndDateDT);
            setTimeout(() => {
                dischargeDateSelectorRefresh();
            }, 50);
            showInfo(
                "End date has been set to " +
                    FallBackEncounterDurationMins +
                    " minutes later than start date.",
            );
        }
    };

    const clearDischargeDate = () => {
        if (!timeObj.endDate) timeObj.endTime = null;
    };

    // HELPER FUNCTIONS
    const _openSwitchItemTypeModal = (
        itemType = Number(__DEFAULT_ITEM_TYPE__),
    ) => {
        selectedItemType = Number(itemType);
        isSwitchItemTypeModalOpen = true;
    };

    const _closeSwitchItemTypeModal = () => {
        isSwitchItemTypeModalOpen = false;
        selectedItemType = Number(__DEFAULT_ITEM_TYPE__);
    };

    const _openModifyItemForm = (itemDetails = {}) => {
        showModifyItemForm = true;
        selectedItem = { ...itemDetails };
    };

    const _closeModifyItemForm = () => {
        showModifyItemForm = false;
        selectedItem = {};
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-end gap-x-2">
                        <div class="addIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit item"}">
                            <i class="addIcon ${editIcon}"></i>
                        </div>

                        <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Remove item"}">
                            <i class="deleteIcon ${deleteIcon}"></i>
                        </div>
                    </div>`;
    };
</script>

<NotificationModal
    isModalOpen={isSelectorOpen}
    title="Free Text Items"
    modalMaxWidth="max-w-6xl"
    showConfirmBtn={false}
    showCancelBtn={false}
    {btnOptions}
    {headerOptions}
    isConfirming={isSavingItems}
    closeModalFunction={closeModal}
    preventESCClose={showModifyItemForm}
    noContentPadding
>
    <div class="flex flex-col gap-y-2 relative min-h-[300px] overflow-hidden p-4">
        <div class="content flex flex-col flex-1 gap-y-4 overflow-y-auto">
            <div class="flex items-center justify-between px-3 mt-2">
                <p class="font-bold">
                    Procedure Name: {getText(itemCodeObj.procedureName)}
                </p>

                <button
                    class="btn btn-sm btn-secondary flex items-center gap-x-2"
                    on:click={_openModifyItemForm}
                >
                    <i class={addIcon} />
                    <p class="pt-1">Add New</p>
                </button>
            </div>

            {#if !selectedItems.length}
                <div class="flex flex-1 items-center justify-center">
                    No items
                </div>
            {:else}
                <SvelteTable
                    {columns}
                    rows={[
                        ...selectedItems,
                        getSumOfColumnValues(
                            selectedItems,
                            freeTextWithGstCharge,
                        ),
                    ]}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                />
            {/if}
        </div>
        {#if itemCodeObj.itemType == "invoice" && patientEncounterDetails.scheduledDate}
            <div class="flex w-full items-center gap-x-4">
                <div class="flex flex-1 items-center gap-x-2 px-2">
                    <p class="cursor-default">Procedure start time:</p>
                    <div class="flex flex-1 gap-x-2 items-center">
                        <input
                            type="time"
                            bind:value={timeObj.startTime}
                            class="input input-sm input-bordered flex-1"
                            on:input={compareAndFixDischargeDate}
                        />
                    </div>
                </div>

                <div class="flex flex-1 items-center gap-x-2 px-2">
                    <p class="cursor-default">Procedure end time:</p>
                    <div class="flex flex-1 gap-x-2 items-center">
                        <input
                            type="time"
                            bind:value={timeObj.endTime}
                            class="input input-sm input-bordered flex-1"
                        />
                    </div>
                </div>
            </div>
        {/if}
        <!-- Charge patient options -->
        {#if showChargePatientSelector}
            <div class="flex items-center justify-end gap-x-4">
                <p class="leading-6 text-sm">Charge patient:</p>
                <div class="flex items-center gap-x-4">
                    <button
                        class={`btn btn-sm rounded rounded-full text-xs ${
                            itemCodeObj.oopType == 0
                                ? "btn-primary"
                                : "btn-secondary"
                        } ${showChargePatientSelector ? "" : "cursor-not-allowed btn-active btn-ghost"}`}
                        on:click|preventDefault={() =>
                            (itemCodeObj.oopType = 0)}
                    >
                        Before
                    </button>
                    <button
                        class={`btn btn-sm rounded rounded-full text-xs ${
                            itemCodeObj.oopType == 1
                                ? "btn-primary"
                                : "btn-secondary"
                        } ${showChargePatientSelector ? "" : "cursor-not-allowed btn-active btn-ghost"}`}
                        on:click|preventDefault={() =>
                            (itemCodeObj.oopType = 1)}
                    >
                        After
                    </button>
                </div>
            </div>
        {/if}

        <ModifyFreeTextItemModal
            isModalOpen={showModifyItemForm}
            itemObj={selectedItem}
            closeModal={_closeModifyItemForm}
            confirmAction={modifyItem}
        />
    </div>
</NotificationModal>

<ChangeItemTypeModal
    isModalOpen={isSwitchItemTypeModalOpen}
    {selectedItemType}
    confirmAction={_closeSwitchItemTypeModal}
    closeModal={() => confirmSwitchItemType(selectedItemType)}
    closeModalFunction={_closeSwitchItemTypeModal}
/>
